import { RegisterScreen } from '@/components/RegisterScreen';
import { Wand2 } from 'lucide-react';
import { useRows } from '@/hooks/useResource';
import { soilAdvice } from '@/lib/soilAdvice';
import { toast } from '@/lib/toast';
import { formatDate, formatNumber, todayIso } from '@/components/live';
import { plotCell, plotField, usePracticePickers } from '@/screens/parts/practiceFields';
import { SAMPLES, SOIL_TEXTURES, optionLabel } from '@/lib/practiceLists';

interface Row {
  id: string; soil_test_code: string | null; plot_code: string | null; farmer_name: string | null; village_name: string | null;
  sampled_on: string; report_date: string | null; laboratory: string | null; report_no: string | null; soil_texture: string | null;
  ph: number | null; organic_carbon_pct: number | null; nitrogen_kg_ha: number | null; phosphorus_kg_ha: number | null;
  potassium_kg_ha: number | null; valid_from: string | null; valid_to: string | null; validity_state: string; document_id: string | null;
}

/**
 * Every soil health card and laboratory report on file (owner, 2026-09-25: "these all make as ui
 * pages under Plot management").
 *
 * A test is a reading on the day it was sampled, not a document with an expiry (owner,
 * 2026-09-27); an old one is flagged by its age, and "Suggest from results" drafts the recommended
 * application from the readings.
 */
const num = (value: unknown) => (String(value ?? '').trim() === '' ? null : Number(value));

export default function SoilTestsScreen() {
  const pick = usePracticePickers();
  const used = useRows<{ laboratory: string | null }>('plot_soil_tests', { orderBy: { column: 'laboratory' } });
  const laboratories = [...new Set([...(used.data ?? []).map((row) => row.laboratory).filter((name): name is string => Boolean(name)), ...SAMPLES.laboratory])]
    .map((name) => ({ id: name, label: name }));
  const threeYearsAgo = new Date(Date.now() - 3 * 365.25 * 86400000).toISOString().slice(0, 10);

  return (
    <RegisterScreen<Row>
      plotFilter
      addLabel="Add report" exportName="soil-tests"
      orderBy={{ column: 'sampled_on', ascending: false }}
      recordLabel={(row) => `soil test ${row.soil_test_code ?? ''} on ${row.plot_code ?? ''}`}
      screenKey="plot.mapping"
      searchColumns={['soil_test_code', 'plot_code', 'farmer_name', 'laboratory', 'report_no']}
      subtitle="Soil health cards and laboratory reports, and the application their results call for."
      table="ops.plot_soil_tests" title="Soil test reports" view="plot_soil_tests"
      columns={[
        { key: 'soil_test_code', label: 'Ref', render: (row) => <strong>{row.soil_test_code ?? '--'}</strong> },
        { key: 'plot_code', label: 'Plot', render: plotCell },
        { key: 'village_name', label: 'Village', render: (row) => row.village_name ?? '--' },
        { key: 'sampled_on', label: 'Sampled', render: (row) => formatDate(row.sampled_on) },
        { key: 'laboratory', label: 'Laboratory', render: (row) => (
          <span className="cell-stack">
            <strong>{row.laboratory ?? '--'}</strong>
            <small className="muted">{row.report_no ?? ''}</small>
          </span>
        ) },
        { key: 'ph', label: 'pH', align: 'right', render: (row) => (row.ph == null ? '--' : formatNumber(row.ph, 1)) },
        { key: 'organic_carbon_pct', label: 'OC %', align: 'right',
          render: (row) => (row.organic_carbon_pct == null ? '--' : formatNumber(row.organic_carbon_pct, 2)) },
        { key: 'nitrogen_kg_ha', label: 'N-P-K (kg/ha)', align: 'right', render: (row) => (
          [row.nitrogen_kg_ha, row.phosphorus_kg_ha, row.potassium_kg_ha].every((value) => value == null)
            ? '--'
            : [row.nitrogen_kg_ha, row.phosphorus_kg_ha, row.potassium_kg_ha].map((value) => (value == null ? '--' : formatNumber(value, 0))).join(' - ')
        ) },
        { key: 'soil_texture', label: 'Texture', render: (row) => (row.soil_texture ? optionLabel(SOIL_TEXTURES, row.soil_texture) : '--') },
      ]}
      stats={[
        // A test has no expiry (owner, 2026-09-27); how old it is is what matters.
        { key: 'old', label: 'Sampled over 3 years ago', tone: 'warn', match: (row) => row.sampled_on < threeYearsAgo },
        // The report is the evidence; the numbers typed from it are an aid. An FSC auditor asks
        // for the card, so a row without one is worth seeing on its own.
        { key: 'no_document', label: 'No report attached', tone: 'warn', match: (row) => !row.document_id },
        { key: 'acidic', label: 'pH below 5.5', tone: 'info', match: (row) => row.ph != null && row.ph < 5.5 },
      ]}
      fields={[
        plotField(pick.plots),
        { key: 'sampled_on', label: 'Sample taken on', type: 'date', required: true, max: todayIso() },
        { key: 'report_date', label: 'Report date', type: 'date' },
        // A list of the laboratories already used and the usual ones, with "+ Add new..." for another
        // (owner, 2026-09-27: "laboratory can be dropdown with add feature").
        { key: 'laboratory', label: 'Laboratory', type: 'select', allowNew: true, options: laboratories },
        { key: 'report_no', label: 'Report / card no.', type: 'text', placeholder: 'e.g. SHC/WB/2026/004512' },
        // With the report's other particulars, not on a tab of its own (owner, 2026-09-27: "Upload of
        // report can be general").
        { key: 'document_id', label: 'Report / soil health card', type: 'file', wide: true },
        { key: 'soil_texture', label: 'Texture', type: 'select', options: SOIL_TEXTURES },
        { key: 'sample_depth_cm', label: 'Sample depth (cm)', type: 'number' },
        { key: 'ph', label: 'pH', type: 'number', section: 'Results' },
        { key: 'ec_ds_per_m', label: 'EC (dS/m)', type: 'number' },
        { key: 'organic_carbon_pct', label: 'Organic carbon (%)', type: 'number' },
        { key: 'nitrogen_kg_ha', label: 'Nitrogen N (kg/ha)', type: 'number' },
        { key: 'phosphorus_kg_ha', label: 'Phosphorus P (kg/ha)', type: 'number' },
        { key: 'potassium_kg_ha', label: 'Potassium K (kg/ha)', type: 'number' },
        { key: 'sulphur_ppm', label: 'Sulphur S (ppm)', type: 'number' },
        { key: 'zinc_ppm', label: 'Zinc Zn (ppm)', type: 'number' },
        { key: 'iron_ppm', label: 'Iron Fe (ppm)', type: 'number' },
        { key: 'manganese_ppm', label: 'Manganese Mn (ppm)', type: 'number' },
        { key: 'copper_ppm', label: 'Copper Cu (ppm)', type: 'number' },
        { key: 'boron_ppm', label: 'Boron B (ppm)', type: 'number' },
        { key: 'recommendations', label: 'Recommendations', type: 'textarea',
          hint: 'Use "Suggest from results" above to draft this from the readings; check it against the laboratory\'s advice.' },
        { key: 'remarks', label: 'Remarks', type: 'textarea' },
      ]}
      toolbar={(form, setForm) => (
        <div className="toolbar-row">
          <button onClick={() => {
                    const advice = soilAdvice({
                      ph: num(form.ph), ec: num(form.ec_ds_per_m), oc: num(form.organic_carbon_pct),
                      n: num(form.nitrogen_kg_ha), p: num(form.phosphorus_kg_ha), k: num(form.potassium_kg_ha),
                      s: num(form.sulphur_ppm), zn: num(form.zinc_ppm), fe: num(form.iron_ppm), mn: num(form.manganese_ppm),
                      cu: num(form.copper_ppm), b: num(form.boron_ppm),
                    });
                    if (advice.length === 0) { toast.error('Enter the results first (Results tab).'); return; }
                    setForm((now) => ({ ...now, recommendations: advice.map((line) => `- ${line}`).join('\n') }));
                    toast.success('Recommendations drafted from the results. Check them on the Results tab.');
                  }}
                  type="button"><Wand2 size={15} /> Suggest from results</button>
          <span className="muted">Standard Soil Health Card ratings for pH, EC, organic carbon, N-P-K, S and micronutrients.</span>
        </div>
      )}
      validate={(form) => ({
        ph: form.ph && (Number(form.ph) < 0 || Number(form.ph) > 14) ? 'pH is between 0 and 14.' : undefined,
        report_date: form.report_date && String(form.report_date) < String(form.sampled_on ?? '') ? 'The report cannot be before the sample.' : undefined,
      })}
    />
  );
}
